import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ITankMoveListItem, ITanksToMoveListsResponse } from '@interfaces/tankMoveListItem';
import { TranslateService } from '@ngx-translate/core';
import { AppDialogService } from '@services/app-dialog.service';
import { LogService } from '@services/log.service';
import { DataManager, Query } from '@syncfusion/ej2-data';
import { Observable, of, tap } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { DSRESTClient } from 'src/app/shared/communication-objects/restclient.service';
import { TankMoveStatusComponent } from '../components/tank-move-status/tank-move-status.component';
import { TankMoveComponent } from '../tank-move.component';
import { deviceTypeCategory } from '@interfaces/deviceTypeCategory';
import { TokenService } from '@services/token.service';
import { AuthUrlAdaptor } from 'src/app/shared/communication-objects/auth-url.adaptor';

export type MoveTankFormModes = 'tank' | 'location' | 'organization';

@Injectable()
export class MoveTankService {
  private readonly API_URL: string = 'MoveTanks';
  private readonly ERROR_RETRIEVING_DATA: string = 'Error retrieving the data...';
  private readonly ERROR_MOVING_TANKS: string = 'Error moving the tanks...';
  /**
   * Dialog geometry. Width is a viewport width with a pixel CAP, not a percentage of the screen:
   * a percentage keeps the same proportion of margin at every size, so the narrower the window the
   * more of it is spent on whitespace - which is backwards, because that is when the content needs
   * the room. `95vw` is effectively full width with the app's usual gutter, and the cap is what stops
   * the form stretching on a wide monitor. Same pattern as the bulk-update dialogs on the
   * organization tank setup grid.
   *
   * The caps are the widths these dialogs already resolved to on a 1920px monitor, so wide screens
   * look as they did and only narrow ones change.
   */
  private readonly POP_UP_WIDTH = '95vw';
  private readonly POP_UP_HEIGHT = '95%';

  /** The dual-listbox form: two tank lists and the destination picker. */
  private readonly POP_UP_MAX_WIDTH = '1600px';

  /** The dual-tree form: an organization tree, the selection tree, and the destination picker. */
  private readonly TREE_POP_UP_MAX_WIDTH = '1400px';

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private appDialogService: AppDialogService,
    private tokenService: TokenService
  ) {}

  /**
   * Retrieves tanks for the specified organization and location IDs.
   * @param orgId - Organization ID.
   * @param locationIds - Array of location IDs.
   */
  getTanksForLocationIds(orgId: number, locationIds: number[]): Promise<{ tanks: any[] }> {
    return  firstValueFrom(this.restClient.postDirect<any>(
      `${this.API_URL}/tree-view-data/${orgId}`,
      { locationIds }
    ));
  }

  /**
   * Retrieves the list of tanks to move based on unique IDs.
   * @param uniqueIds - Array of unique tank IDs.
   * @returns Observable containing the list of tanks to move.
   */
  getTanksToMoveLists(uniqueIds: number[]): Observable<ITanksToMoveListsResponse> {
    // Guard clause for empty or null uniqueIds
    if (!uniqueIds || uniqueIds.length === 0) {
      return of({ selectedTanks: [], associatedTanks: [] });
    }

    // Retrieve the tanks to move lists from the server
    return this.restClient
      .getDirect<ITanksToMoveListsResponse>(
        `${this.API_URL}/tanks-to-move-lists/?uniqueIds=${uniqueIds.join(',')}`
      )
      .pipe(
        this.handleError(`getTanksToMoveLists(${uniqueIds.join(',')})`, this.ERROR_RETRIEVING_DATA)
      ) as Observable<ITanksToMoveListsResponse>;
  }

  getTanksToMoveTreeViewDataManager(organizationId: number, locationId: number): DataManager {
    const url = `api/v1/${this.API_URL}/tree-view-data/${organizationId}`;
    const adapter = new LocTankMoveUrlAdaptor(this.tokenService, {
      locationId: !locationId ? null : locationId.toString(),
    });

    return new DataManager({
      url: url,
      adaptor: adapter,
    });
  }

  getLocationsForOrganizations(
    organizationIds: number[],
    locationIdToExclude?: number
  ): Promise<{ organizations: any[]; locations: any[]; tanks: any[] }> {
    if (!organizationIds?.length) {
      return Promise.resolve({ organizations: [], locations: [], tanks: [] });
    }
    const body = {
      organizationIds,
      locationIdToExclude: locationIdToExclude ?? null,
    };
    return firstValueFrom(
      this.restClient.postForResponse<any>(`${this.API_URL}/tree-view-data/locations-batch`, body)
    ).then((res) => ({
      organizations: res?.Organizations ?? res?.organizations ?? [],
      locations: res?.Locations ?? res?.locations ?? [],
      tanks: res?.Tanks ?? res?.tanks ?? [],
    }));
  }

  getReceivingLocationsOnly(organizationId: number): Promise<{ organizations: any[]; locations: any[] }> {
    if (organizationId == null || Number.isNaN(organizationId)) {
      return Promise.resolve({ organizations: [], locations: [] });
    }
    return firstValueFrom(
      this.restClient.getDirect<any>(`${this.API_URL}/tree-view-data/receiving-locations/${organizationId}`)
    ).then((res) => ({
      organizations: res?.Organizations ?? res?.organizations ?? [],
      locations: res?.Locations ?? res?.locations ?? [],
    }));
  }

  moveTanks(selectedTanks: ITankMoveListItem[], selectedLocationId: number): Observable<any> {
    //guard clause
    if (!selectedTanks || selectedTanks.length === 0) return of(null);

    //move the tanks
    return this.restClient
      .postForResponse<any>(`${this.API_URL}/move-tanks`, {
        tankIds: selectedTanks.map(t => t.tankId),
        selectedLocationId: selectedLocationId,
      })
      .pipe(
        this.handleError(
          `moveTanks(${selectedTanks}, ${selectedLocationId})`,
          this.ERROR_MOVING_TANKS
        )
      );
  }

  showMoveTankDialog(
    formMode: MoveTankFormModes,
    uniqueId: number | null,
    locationId: number | null,
    organizationId: number | null,
    billableOrganizationId: number | null,
    tankSourceOrganizationId: number | null,
    onMoveComplete: () => void = null
  ): MatDialogRef<TankMoveComponent> {
    //Only the CAP varies by form mode now - the two trees need less room than the two listboxes.
    //Both fill the window below their cap rather than holding a percentage of it.
    const maxWidth =
      formMode === 'location' || formMode === 'organization'
        ? this.TREE_POP_UP_MAX_WIDTH
        : this.POP_UP_MAX_WIDTH;

    return this.dialog.open(TankMoveComponent, {
      data: {
        formMode,
        uniqueId,
        locationId,
        organizationId,
        billableOrganizationId,
        tankSourceOrganizationId,
        onMoveCompleteCallback: onMoveComplete,
      },
      panelClass: 'cso-dialog',
      width: this.POP_UP_WIDTH,
      maxWidth: maxWidth,
      height: this.POP_UP_HEIGHT,
    });
  }

  /**
   * What the pending move would do to the selected tanks' affiliation. Writes nothing.
   *
   * The portal now decides this rather than leaving it to CommPktMgr's next pass, so it can also say
   * what it is about to decide - which matters most for the case product calls common: a tank
   * deliberately unshared by hand re-conforms to the destination's auto value on any move, a move
   * inside the same organization included.
   */
  getAffiliationPreview(
    selectedTanks: ITankMoveListItem[],
    selectedLocationId: number,
  ): Observable<IMoveTanksAffiliationPreview> {
    if (!selectedTanks || selectedTanks.length === 0) return of(null);

    return this.restClient
      .postForResponse<IMoveTanksAffiliationPreview>(
        `${this.API_URL}/move-tanks/affiliation-preview`,
        {
          tankIds: selectedTanks.map(t => t.tankId),
          selectedLocationId: selectedLocationId,
        }
      )
      .pipe(
        this.handleError('getAffiliationPreview', this.ERROR_RETRIEVING_DATA)
      ) as Observable<IMoveTanksAffiliationPreview>;
  }

  showConfirmationDialog(): Observable<boolean> {
    return this.appDialogService.showExcplicitConfirmationDialog({
      explicitConfirmationText: 'MoveForms.TankMove.ConfirmationText',
      title: 'MoveForms.TankMove.MoveTank',
      messages: [
        {
          message: 'MoveForms.TankMove.ConfirmationSubtitle',
          icon: 'warning',
          color: 'red',
        },
        {
          message: 'MoveForms.TankMove.ConfirmationRatePlanMessage',
          icon: 'warning',
          color: 'red',
        },
        // Affiliation belongs in the same list as permissions and rate plans: it is another thing the
        // move resets without being asked to. Stated unconditionally here because this dialog is one
        // fixed list; the COUNTS are inline on the form behind it, and only when something would
        // actually change.
        {
          message: 'MoveForms.TankMove.ConfirmationAffiliationMessage',
          icon: 'warning',
          color: 'red',
        },
      ],
      cancelButtonText: 'Dialog.Cancel',
      confirmButtonText: 'MoveForms.TankMove.MoveTank',
      inputLabelSuffix: 'MoveForms.TankMove.ExplicityConfirmationLabelSuffix',
    });
  }

  showMoveStatusDialog(result: {
    selectedTanks: any[];
  }): MatDialogRef<TankMoveStatusComponent, { sendToList: boolean }> {
    return this.dialog.open(TankMoveStatusComponent, {
      panelClass: 'cso-dialog',
      //Same rule as the form above: fill a narrow window, cap on a wide one. This grid is wider than
      //the form - it carries before/after for location, organization, billable and affiliation.
      width: this.POP_UP_WIDTH,
      maxWidth: this.POP_UP_MAX_WIDTH,
      data: { results: result.selectedTanks, showToListButton: true, exportToExcelFileName : 'Move_Tank_Export' },
    });
  }

  private handleError(methodName: string, errorMessage: string) {
    return tap({
      error: (response: any) => {
        this.logger.error(`move-tank-service.ts: ${methodName}`);
        this.appDialogService.showErrorMessage(errorMessage, response);
      },
    });
  }
}

/**
 * What a pending tank move would do to affiliation. Shares its shape with the Move Locations preview,
 * because it is the same question counted over a different set - see MoveLocationsAffiliationPreviewDTO.
 */
export interface IMoveTanksAffiliationPreview {
  destinationOrgId: number;
  destinationOrgName: string;
  destinationAffiliateOrgId: number;
  destinationAffiliateName: string;

  /** Unaffiliated today, affiliated after the move. */
  tanksGainingAffiliation: number;

  /** Affiliated today, unaffiliated after the move. */
  tanksLosingAffiliation: number;

  /** Affiliated today to a different organization than the move will assign. */
  tanksChangingAffiliation: number;

  /** Already holding exactly what the move would assign, including nothing. */
  tanksUnchanged: number;

  /** False when the move leaves every tank's affiliation alone - the prompt is skipped entirely. */
  hasAffiliationChanges: boolean;
}

export interface IMoveTankTreeViewData {
  organizations: TreeNode[];
  locations: TreeNode[];
  tanks: TreeNode[];
}

export interface TreeNode {
  id: string;
  name: string;
  level: number;

  parentId?: string;
  hasChildren?: boolean;
  expanded?: boolean;
  isChecked?: string | boolean;
  icon?: string;
  childCount: number;
  areChildrenLoaded?: boolean;
  toggle?: boolean;

  isMatch?: boolean;
  matchEnd?: number;
  matchStart?: number;
  cssClass?: string;

  dtc?: deviceTypeCategory;
}

//Custom UrlAdaptor to add default parameters to the query (e.g. locationId).
//Extends AuthUrlAdaptor, not UrlAdaptor: MoveTanksController is [Authorize] plus two policies, and
//a DataManager's request never passes through JwtInterceptor.
export class LocTankMoveUrlAdaptor extends AuthUrlAdaptor {
  private defaultParams: { [key: string]: string };

  constructor(tokenService: TokenService, defaultParams: { [key: string]: string }) {
    super(tokenService);
    this.defaultParams = defaultParams;
  }

  processQuery(dm: DataManager, query: Query, hierarchyFilters?: any): Object {
    // Add default parameters to the query
    Object.keys(this.defaultParams).forEach(key => {
      query.addParams(key, this.defaultParams[key]);
    });

    // Call the base class's processQuery method
    return super.processQuery(dm, query, hierarchyFilters);
  }
}

export interface TankMoveListItemDTO {
  tankId: number;
  name: string;
  locationName: string;
  billableOrgName: string;
  orgName: string;
  isAssociatedTank: boolean;
  isDifferent: boolean | null;
  billableOrgId: number;
  orgId: number;
  locationId: number;
  minNumber: number;
  parentTankId: number;
  locationGUID: string;
  orgGUID: string;
  tankGUID: string;
  deviceCategory: deviceTypeCategory;
}

export interface TanksToMoveListsResponseDTO {
  selectedTanks: TankMoveListItemDTO[];
  associatedTanks: TankMoveListItemDTO[];
}
